<aside
  class="flex flex-col bg-side-drawer-bg"
  [ngClass]="asideNgClass()"
>
  @if (drawerDisplayArtist(); as artist) {
    <div
      class="relative flex h-full min-h-0 flex-1 flex-col bg-side-drawer-bg text-text-primary"
    >
      <!-- Top Bar: Status / Role + Close Button (12px padding) -->
      <div class="flex items-center justify-between px-3 pt-3 pb-0 shrink-0">
        <span class="text-xs font-medium text-text-dimmed">
          Artist<span class="mx-1.5 text-text-dimmed/60">·</span>Active since
          {{ artist.activeSince || '2016' }}
        </span>
        <button
          type="button"
          (click)="closeSidebar()"
          class="rounded-full p-1.5 text-text-dimmed hover:text-text-primary hover:bg-white/10 transition-colors cursor-pointer"
          aria-label="Close"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- Scrollable Main Content (12px padding) -->
      <div id="artist-profile-preview-drawer-scroll" class="custom-scrollbar min-h-0 flex-1 overflow-y-auto overflow-x-clip px-3 pb-2 pt-1">
        <div class="flex flex-col gap-4">
          
          <!-- Artist Header (110px Circular Avatar + Info) -->
          <div class="flex items-start gap-3.5">
            <img
              [src]="artist.imageUrl || drawerImageFallback(artist.name)"
              (error)="onDrawerImageError($event, artist.name)"
              class="h-[110px] w-[110px] shrink-0 rounded-full border border-white/10 bg-zinc-800 object-cover shadow-lg"
              [alt]="artist.name"
            />
            <div #drawerNameMeasureHost class="flex min-w-0 flex-1 flex-col gap-1">
              <h3
                #drawerNameHeading
                class="font-bold leading-tight tracking-tight text-text-primary text-2xl truncate"
              >
                {{ artist.name }}
              </h3>
              <!-- Musical Genres using BadgeComponent -->
              <div class="flex flex-wrap items-center gap-1.5 mt-1">
                @for (genre of displayedGenres(); track genre) {
                  <app-badge variant="neutral" size="tiny" [label]="genre" />
                }
                @if (extraGenresCount() > 0) {
                  <app-badge variant="neutral" size="tiny" [label]="'+ ' + extraGenresCount()" />
                }
              </div>
            </div>
          </div>

          <!-- HypeTrack Score -->
          <div class="rounded-md bg-card-bg-default p-3">
            <div class="flex items-center justify-between">
              <p class="text-xs text-text-half-dimmed font-normal">HypeTrack Score</p>
              @if (!drawerLoading() && hypeScore(); as score) {
                <span
                  class="text-xs font-semibold tabular-nums"
                  [class]="scoreChangeClass(score.change)"
                  title="Change over the last 7 days"
                >
                  {{ formatScoreChange(score.change) }}
                  <span class="font-normal text-text-dimmed">7d</span>
                </span>
              }
            </div>
            @if (drawerLoading()) {
              <div class="h-6 w-16 bg-card-bg-hover animate-pulse rounded mt-1.5"></div>
              <div class="h-1.5 w-full bg-card-bg-hover animate-pulse rounded-full mt-2.5"></div>
            } @else if (hypeScore(); as score) {
              <p class="mt-1 text-[20px] font-bold tracking-tight text-text-primary leading-tight tabular-nums">
                {{ score.value }}<span class="text-xs font-medium text-text-dimmed"> / 100</span>
              </p>
              <div class="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-text-inverted-primary">
                <div class="h-full rounded-full bg-text-brand" [style.width.%]="score.value"></div>
              </div>
            } @else {
              <p class="mt-1 text-[20px] font-bold tracking-tight text-text-primary leading-tight">-</p>
            }
          </div>

          <!-- 4 Metric Cards (2x2 Grid) with 20px numbers -->
          <div class="grid grid-cols-2 gap-2">
            <!-- 1. Monthly Listeners -->
            <div class="rounded-md bg-card-bg-default p-3 flex flex-col justify-center">
              @if (drawerLoading()) {
                <div class="h-6 w-20 bg-card-bg-hover animate-pulse rounded mb-1"></div>
              } @else {
                <p class="text-[20px] font-bold tracking-tight text-text-primary leading-tight">
                  {{ monthlyListenersFormatted() }}
                </p>
              }
              <p class="text-xs text-text-half-dimmed font-normal">Monthly Listeners</p>
            </div>

            <!-- 2. Total Tracks -->
            <div class="rounded-md bg-card-bg-default p-3 flex flex-col justify-center">
              @if (drawerLoading()) {
                <div class="h-6 w-14 bg-card-bg-hover animate-pulse rounded mb-1"></div>
              } @else {
                <p class="text-[20px] font-bold tracking-tight text-text-primary leading-tight">
                  {{ totalTracksFormatted() }}
                </p>
              }
              <p class="text-xs text-text-half-dimmed font-normal">Total Tracks</p>
            </div>

            <!-- 3. Hypetrack / IFPI Rank -->
            <div class="rounded-md bg-card-bg-default p-3 flex flex-col justify-center">
              @if (drawerLoading()) {
                <div class="h-6 w-12 bg-card-bg-hover animate-pulse rounded mb-1"></div>
              } @else {
                <p class="text-[20px] font-bold tracking-tight text-text-primary leading-tight">
                  {{ rankFormatted() }}
                </p>
              }
              <p class="text-xs text-text-half-dimmed font-normal">IFPI Rank</p>
            </div>

            <!-- 4. Last Release -->
            <div class="rounded-md bg-card-bg-default p-3 flex flex-col justify-center">
              @if (drawerLoading()) {
                <div class="h-6 w-24 bg-card-bg-hover animate-pulse rounded mb-1"></div>
              } @else {
                <p class="text-[20px] font-bold tracking-tight text-text-primary leading-tight truncate">
                  {{ lastReleaseFormatted() }}
                </p>
              }
              <p class="text-xs text-text-half-dimmed font-normal">Last Release</p>
            </div>
          </div>

          <!-- Audience Section -->
          <div class="space-y-1.5">
            <h4 class="text-xs font-medium text-text-dimmed">Audience</h4>
            <div class="space-y-0.5">
              @if (drawerLoading()) {
                @for (i of [1, 2, 3, 4]; track i) {
                  <div class="flex items-center justify-between py-1.5 px-1">
                    <div class="h-4 w-20 bg-card-bg-hover animate-pulse rounded"></div>
                    <div class="h-4 w-12 bg-card-bg-hover animate-pulse rounded"></div>
                  </div>
                }
              } @else {
                @for (item of audienceList(); track item.id) {
                  <div
                    [class]="'flex items-center justify-between py-1.5 px-1 rounded transition-colors ' + (item.canOpen ? 'cursor-pointer hover:bg-white/4' : '')"
                    (click)="item.canOpen && openDrawerPlatform(item.id)"
                  >
                    <span class="text-sm font-medium text-text-half-dimmed">{{ item.label }}</span>
                    <span class="text-sm font-semibold text-text-primary tabular-nums">{{ item.value }}</span>
                  </div>
                }
              }
            </div>
          </div>

          <!-- Recent Releases Section -->
          <div class="space-y-2">
            <h4 class="text-xs font-medium text-text-dimmed">Recent Releases</h4>
            <div class="space-y-2">
              @if (drawerLoading()) {
                @for (i of [1, 2, 3, 4]; track i) {
                  <div class="rounded-md border border-card-border-border p-2.5 flex items-center justify-between gap-3 animate-pulse">
                    <div class="flex items-center gap-3 min-w-0">
                      <div class="w-11 h-11 rounded-lg bg-card-bg-hover shrink-0"></div>
                      <div class="min-w-0 space-y-1.5">
                        <div class="h-4 w-28 bg-card-bg-hover rounded"></div>
                        <div class="h-3 w-16 bg-card-bg-hover rounded"></div>
                      </div>
                    </div>
                    <div class="h-5 w-16 bg-card-bg-hover rounded-full shrink-0"></div>
                  </div>
                }
              } @else if (recentReleases().length > 0) {
                @for (release of recentReleases(); track release.id) {
                  <div class="rounded-md border border-card-border-border p-2.5 flex items-center justify-between gap-3 hover:border-zinc-600/50 transition-colors">
                    <div class="flex items-center gap-3 min-w-0">
                      <img
                        [src]="release.imageUrl || drawerImageFallback(release.title)"
                        (error)="onDrawerImageError($event, release.title)"
                        class="w-11 h-11 rounded-lg object-cover bg-zinc-800 shrink-0 border border-white/5"
                        [alt]="release.title"
                      />
                      <div class="min-w-0">
                        <p class="text-sm font-semibold text-text-primary truncate max-w-[130px] sm:max-w-[150px] leading-snug">
                          {{ release.title }}
                        </p>
                        <p class="text-xs text-text-half-dimmed mt-0.5 font-normal">
                          {{ formatRelativeReleaseDate(release.releaseDate) }}
                        </p>
                      </div>
                    </div>
                    <!-- Plays badge using BadgeComponent -->
                    <div class="shrink-0">
                      <app-badge variant="neutral" size="tiny" [label]="release.streams != null ? ((release.streams | number) + ' Plays') : '- Plays'" />
                    </div>
                  </div>
                }
              } @else {
                <p class="text-xs text-text-dimmed py-2">No recent releases available.</p>
              }
            </div>
          </div>

        </div>
      </div>

      <!-- Footer Button: View Artist Profile (12px padding) -->
      <div class="p-3 pt-2 shrink-0 bg-side-drawer-bg">
        <app-button
          variant="primary"
          size="large"
          class="w-full block"
          className="w-full justify-center"
          label="View Artist Profile"
          (btnClick)="navigateToArtistProfile(artist)"
        />
      </div>

    </div>
  }
</aside>
